import { fromApiMoney, type Money, toApiMoney } from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { forgetOnFinalClientError } from '../http/idempotency.ts'

export interface ForgiveDebtInput {
  /** Omitted: forgives the full outstanding balance. */
  readonly amount?: Money
}

export interface ForgiveDebtResult {
  readonly creditorEntryId: string
  readonly debtorEntryId: string
  readonly amount: Money
}

export interface CrossLedgerDebtsRepository {
  /** Final and irreversible - requires `TRANSFER` in both ledgers. */
  forgive(ledgerId: string, otherLedgerId: string, input: ForgiveDebtInput): Promise<ForgiveDebtResult>
}

export function createCrossLedgerDebtsRepository(client: TalerClient): CrossLedgerDebtsRepository {
  return {
    async forgive(ledgerId, otherLedgerId, input) {
      const body: Schemas['ForgiveDebtRequest'] = {
        ...(input.amount === undefined ? {} : { amount: toApiMoney(input.amount) as Schemas['ApiMoney'] }),
      }
      const path = `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/cross-ledger-debts/${encodeURIComponent(otherLedgerId)}/forgive`
      const payload = { method: 'POST' as const, path, query: null, body }
      let response: Schemas['ForgiveDebtResponse']
      try {
        response = await client.call<Schemas['ForgiveDebtResponse'], Schemas['ForgiveDebtRequest']>({
          method: 'POST',
          path,
          body,
        })
      } catch (error) {
        forgetOnFinalClientError(client.idempotencyKeys, payload, error)
        throw error
      }
      client.idempotencyKeys.forget(payload)
      return {
        creditorEntryId: response.creditorEntryId,
        debtorEntryId: response.debtorEntryId,
        amount: fromApiMoney(response.amount),
      }
    },
  }
}
